/**
 * Eruda mobile DevTools console — load via CDN script tag, dev only.
 *
 * Why CDN not bundled:
 *   eruda is ~120KB minified. Loading via CDN means it's lazy + cached
 *   across all iSale dev sessions on the same browser AND completely
 *   absent from the production bundle.
 *
 * When it loads:
 *   1. `import.meta.env.DEV` (Vite `pnpm dev` local) — always
 *   2. `VITE_API_URL` baked at build time includes `isale-service-dev` —
 *      so the Pages dev deploy also gets eruda
 *   3. localStorage `isale.eruda=1` (manual override on any env, useful
 *      for prod-side debugging if Sentry's not enough — set in console:
 *      `localStorage.setItem('isale.eruda','1'); location.reload()`)
 *
 * Toggle off: `localStorage.setItem('isale.eruda','0'); location.reload()`.
 *
 * Tap the orange floating button bottom-right to open / close on phone.
 *
 * Repo: https://github.com/liriliri/eruda
 */

const CDN_URL = "https://cdn.jsdelivr.net/npm/eruda@3";

export function maybeInitEruda(): void {
  if (typeof window === "undefined") return;

  // localStorage manual override wins both ways.
  const stored = window.localStorage.getItem("isale.eruda");
  if (stored === "0") return;
  const localOverride = stored === "1";

  const isVite_dev = import.meta.env.DEV;
  const isCloudDev = (import.meta.env.VITE_API_URL ?? "").includes(
    "isale-service-dev",
  );
  if (!localOverride && !isVite_dev && !isCloudDev) return;

  // Don't double-load (e.g. HMR re-runs in dev).
  if ((window as unknown as { eruda?: unknown }).eruda) return;

  const script = document.createElement("script");
  script.src = CDN_URL;
  script.async = true;
  script.onload = () => {
    const eruda = (window as unknown as { eruda?: { init: () => void } }).eruda;
    if (eruda) {
      eruda.init();
      // eslint-disable-next-line no-console
      console.info(
        "[isale:eruda] DevTools console loaded — tap the orange button bottom-right",
      );
    }
  };
  script.onerror = () => {
    // eslint-disable-next-line no-console
    console.warn("[isale:eruda] failed to load from CDN", CDN_URL);
  };
  document.head.appendChild(script);
}
